// Imperative chat-context fold (chat bundle-fold, 2026-08-01).
//
// Chat's ~215KB component tree used to ride the EAGER shell bundle because
// MessengerOverlay was `hide`-mounted at the shell root — created on every
// boot, on every route, drawer open or not. It now mounts behind a
// `_chatCtxFolded` if-latch (workspace/app.js) and the full chat context
// folds HERE, on demand, through the same add-missing mechanism the lazy
// route folds use (foldSharedLibContext — shared/pages/lazyPage.js).
//
// Every opener funnels through these two functions (or ensures first):
// the header Messenger button, openChatFromToast, the sidebar channel-menu
// dispatches, pageContext deep-links, PersonPopover/AppSidebar messageUser,
// meet applySuggestion, and aiWorkspaceOps' headless createChannel /
// sendMessage.
import _wsShared from '@symbo-ls/workspace-shared'
const shellFeatureEnabled = (...args) => _wsShared.functions.featureEnabled(...args)
const shellFoldSharedLibContext = (...args) => _wsShared.pages.foldSharedLibContext(...args)
let _chatFoldInFlight = null

// Fill the shell-root MessengerOverlaySlot with the real overlay AFTER the
// fold registered its definition. DOMQL resolves bare-key extends at parent
// parse time, so app.js cannot mount `MessengerOverlay: {}` directly — the
// slot + post-fold `set` is the same deferred-resolution mechanism lazyPage
// uses for route bodies. Idempotent via the slot's own marker.
const _mountMessengerOverlay = (rootEl) => {
  const slot = rootEl?.MessengerOverlaySlot
  if (!slot || typeof slot.set !== 'function' || slot.__messengerMounted) return
  slot.__messengerMounted = true
  slot.set({
    extends: 'MessengerOverlay',
    // Restates the base component's `_chatDrawerOpen` gate + the /flags
    // feature gates — a mount-level `hide` REPLACES the base hide
    // (verified live 2026-07-25; predicate moved verbatim from app.js).
    hide: (el, s) =>
      !s.root?._chatDrawerOpen ||
      !shellFeatureEnabled(s.root, 'chat') ||
      !shellFeatureEnabled(s.root, 'chat.messengerOverlay')
  })
}

// Fold the full chat context into the live registry (idempotent,
// single-flight, retryable on failure). Returns true when the context is
// registered, false when the fold failed (callers should bail quietly —
// a toast/menu action on a broken chunk must not throw).
export const ensureChatContext = async function () {
  // TWO roots, deliberately distinct: foldSharedLibContext registers into the
  // ELEMENT root's `.context` (same contract as the lazyPage route folds,
  // which pass `el.getRoot()`), while the latch lives on the STATE root.
  // Passing the state root to the fold silently registers nothing — its
  // `.context` is undefined, so both add-missing loops skip (caught live:
  // foldOk=true, zero components registered, the overlay mounting as an
  // empty unknown-key div).
  const rootEl = typeof this?.getRoot === 'function' ? this.getRoot() : this
  const stateRoot = this?.state?.root || rootEl?.state?.root || rootEl?.state || null
  if (!rootEl || !stateRoot) return false
  if (stateRoot._chatCtxFolded) return true
  if (!_chatFoldInFlight) {
    _chatFoldInFlight = import('@symbo-ls/workspace-chat/context.js')
      .then((mod) => {
        const lib = mod?.default || mod
        if (!lib || (!lib.components && !lib.functions)) {
          throw new Error('chat context resolved empty — stale/mismatched bundle')
        }
        shellFoldSharedLibContext(rootEl, lib)
        _mountMessengerOverlay(rootEl)
        // Latch flips ONCE per app lifetime — the registry keeps the folded
        // components across tenant switches, so this key is deliberately NOT
        // in the workspace-scoped wipe registry.
        stateRoot.update({ _chatCtxFolded: true }, { preventFetch: true })
        return true
      })
      .catch((err) => {
        _chatFoldInFlight = null
        console.error('[chat] context fold failed:', err?.message || err)
        return false
      })
  }
  return _chatFoldInFlight
}

// Open the messenger drawer (folding first). Optional channelId focuses a
// conversation; the overlay's own hide-gate owns visibility from here.
export const openMessenger = async function (channelId) {
  const ok = await this.call('ensureChatContext')
  if (!ok) return
  const root = this.state.root
  root.update({
    _chatDrawerOpen: true,
    ...(channelId ? { activeChannelId: channelId } : {})
  })
}
